import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ProductGrid } from '@/features/menu/ProductGrid';
import { ProductCardSkeleton } from '@/features/menu/ProductCardSkeleton';
import { MoodSelector } from '@/features/storefront/MoodSelector';
import { QrOrderBanner } from '@/features/storefront/QrOrderBanner';
import { StorefrontHero } from '@/features/storefront/StorefrontHero';
import {
  useBestSellerProducts,
  usePublicEvents,
  usePublicMoodRecommendations,
  usePublicMoods,
} from '@/features/storefront/discoveryApi';
import {
  moodRecommendationToProduct,
  toStorefrontMood,
  type StorefrontMood,
} from '@/features/storefront/moods';
import type { PublicProduct } from '@/features/menu/types';
import { useBrowseStore } from '@/state/browseStoreStore';

const landingCopy = {
  vi: {
    bestSellerTitle: 'Best Seller',
    newProductsTitle: 'Gợi ý món theo mood',
    viewAllProducts: 'Xem tất cả',
    bestSellersLoading: 'Đang tải Best Seller',
    bestSellersErrorTitle: 'Chưa tải được Best Seller',
    bestSellersEmptyTitle: 'Chưa có dữ liệu Best Seller',
    bestSellersEmptyBody: 'Hiện chưa có xếp hạng sản phẩm công khai từ hệ thống.',
    newProductsLoading: 'Đang tải gợi ý món mới',
    newProductsErrorTitle: 'Chưa tải được gợi ý món mới',
    productsErrorBody:
      'Vui lòng thử lại. Không có giá hoặc tình trạng món nào được suy đoán ở trình duyệt.',
    newProductsEmptyTitle: 'Chưa có gợi ý phù hợp',
    newProductsEmptyBody: 'Hãy thử chọn một mood khác hoặc khám phá thực đơn của cửa hàng.',
    retry: 'Tải lại',
  },
  en: {
    bestSellerTitle: 'Best Seller',
    newProductsTitle: 'New suggestions by mood',
    viewAllProducts: 'View all products',
    bestSellersLoading: 'Loading best sellers',
    bestSellersErrorTitle: 'Unable to load best sellers',
    bestSellersEmptyTitle: 'No best-seller data yet',
    bestSellersEmptyBody: 'There is currently no public product ranking.',
    newProductsLoading: 'Loading new suggestions',
    newProductsErrorTitle: 'Unable to load new suggestions',
    productsErrorBody:
      'Please try again. The browser does not infer product price or availability.',
    newProductsEmptyTitle: 'No matching suggestions',
    newProductsEmptyBody: 'Try another mood or explore a Store menu.',
    retry: 'Retry',
  },
} as const;

const copy = landingCopy.vi;

export default function LandingPage() {
  const selectedStoreCode = useBrowseStore((state) => state.store?.storeCode);
  const moods = usePublicMoods();
  const events = usePublicEvents();
  const bestSellers = useBestSellerProducts(8);
  const [selectedMoodCode, setSelectedMoodCode] = useState<string | null>(null);
  const storefrontMoods = useMemo(
    () =>
      (moods.data ?? [])
        .map(toStorefrontMood)
        .filter((mood): mood is StorefrontMood => Boolean(mood)),
    [moods.data]
  );
  const selectedMood = storefrontMoods.find((mood) => mood.code === selectedMoodCode);
  const moodProducts = usePublicMoodRecommendations(selectedMood?.code, {
    limit: 8,
    enabled: Boolean(selectedMood),
  });
  const suggestedProducts = useMemo(
    () =>
      (moodProducts.data ?? [])
        .map((item) => moodRecommendationToProduct(item, selectedMood?.label))
        .filter((product): product is PublicProduct => Boolean(product)),
    [moodProducts.data, selectedMood?.label]
  );
  const bestSellerProducts = bestSellers.data?.products ?? [];
  const heroProducts = bestSellerProducts.length ? bestSellerProducts : suggestedProducts;

  return (
    <main className="landing-storefront">
      <StorefrontHero store={null} products={heroProducts} events={events.data ?? []} />

      <MoodSelector
        moods={moods.data ?? []}
        loading={moods.isPending}
        error={moods.isError}
        onRetry={() => void moods.refetch()}
        selectedCode={selectedMood?.code}
        onSelectionChange={(mood) => setSelectedMoodCode(mood.code)}
      />

      <section className="highlighted-products" id="menu" aria-labelledby="best-seller-heading">
        <div className="storefront-section-heading">
          <div>
            <h2 id="best-seller-heading">{copy.bestSellerTitle}</h2>
          </div>
          <Link to="/menu">{copy.viewAllProducts}</Link>
        </div>
        {bestSellers.isPending ? (
          <div className="product-grid" aria-label={copy.bestSellersLoading}>
            {Array.from({ length: 4 }, (_, index) => (
              <ProductCardSkeleton key={index} />
            ))}
          </div>
        ) : bestSellers.isError ? (
          <ReadModelState
            icon="!"
            title={copy.bestSellersErrorTitle}
            body={copy.productsErrorBody}
            actionLabel={copy.retry}
            onRetry={() => void bestSellers.refetch()}
          />
        ) : bestSellerProducts.length === 0 ? (
          <ReadModelState
            icon="☕"
            title={copy.bestSellersEmptyTitle}
            body={copy.bestSellersEmptyBody}
          />
        ) : (
          <ProductGrid
            products={bestSellerProducts}
            storeCode={selectedStoreCode}
            browseStoreCode={selectedStoreCode}
            editorialBadge="Best Seller"
          />
        )}
      </section>

      <section
        className="highlighted-products new-product-suggestions"
        id="new-products"
        aria-labelledby="new-products-heading"
      >
        <div className="storefront-section-heading">
          <div>
            <h2 id="new-products-heading">{copy.newProductsTitle}</h2>
          </div>
          {selectedMood && (
            <Link to={`/moods/${encodeURIComponent(selectedMood.code)}`}>
              {copy.viewAllProducts}
            </Link>
          )}
        </div>
        {moods.isPending || (selectedMood && moodProducts.isPending) ? (
          <div className="product-grid" aria-label={copy.newProductsLoading}>
            {Array.from({ length: 4 }, (_, index) => (
              <ProductCardSkeleton key={index} />
            ))}
          </div>
        ) : moods.isError || moodProducts.isError ? (
          <ReadModelState
            icon="!"
            title={copy.newProductsErrorTitle}
            body={copy.productsErrorBody}
            actionLabel={copy.retry}
            onRetry={() => void (moods.isError ? moods.refetch() : moodProducts.refetch())}
          />
        ) : suggestedProducts.length === 0 ? (
          <ReadModelState
            icon="☕"
            title={copy.newProductsEmptyTitle}
            body={copy.newProductsEmptyBody}
          />
        ) : (
          <ProductGrid
            products={suggestedProducts}
            storeCode={selectedStoreCode}
            browseStoreCode={selectedStoreCode}
            editorialBadge="Gợi ý mới"
          />
        )}
      </section>

      <QrOrderBanner />
    </main>
  );
}

function ReadModelState({
  icon,
  title,
  body,
  actionLabel,
  onRetry,
}: {
  icon: string;
  title: string;
  body: string;
  actionLabel?: string;
  onRetry?: () => void;
}) {
  return (
    <section className="menu-empty" role="status">
      <span aria-hidden="true">{icon}</span>
      <h2>{title}</h2>
      <p>{body}</p>
      {actionLabel && onRetry && (
        <button type="button" onClick={onRetry}>
          {actionLabel}
        </button>
      )}
    </section>
  );
}
